import { AlertCircle, FolderKanban, Loader2, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { KanbanBoard } from "@/components/kanban/KanbanBoard";
import { TaskFilterMenu } from "@/components/kanban/TaskFilterMenu";
import { Button } from "@/components/ui/button";
import { useKanbanTasks, type TaskAssigneeFilter } from "@/hooks/use-kanban-tasks";
import { useUserPreferences } from "@/hooks/use-user-preferences";
import { useMe } from "@/hooks/use-me";
import type { TaskDueFilter } from "@/lib/task-due-date";
import { useProjects } from "@/hooks/use-projects";
import { useTags } from "@/hooks/use-tags";
import { useTasks } from "@/hooks/use-tasks";
import { useTenantUsers } from "@/hooks/use-tenant-users";
import { notifyTaskChanged } from "@/lib/project-events";
import type { Project, Task, TaskStatus, TaskTitleSource, TaskUpdateRequest } from "@/lib/project-types";
import { cn, type AccessTokenGetter } from "@/lib/utils";

export interface ProjectKanbanViewProps {
  /** Canonical project UUID from an agent or conversation view configuration. */
  projectId?: string | null;
  getToken: AccessTokenGetter;
  className?: string;
}

/**
 * A view surface for one existing project. The project and its task lanes
 * still come from the normal authenticated Projects API, so normal project
 * visibility rules apply without a duplicate view-data endpoint.
 */
export function ProjectKanbanView({
  projectId,
  getToken,
  className,
}: ProjectKanbanViewProps) {
  const { t } = useTranslation("projects");
  const { get: getProject, error: projectError } = useProjects(getToken, {
    autoLoad: false,
  });
  const [project, setProject] = useState<Project | null>(null);
  const [projectLoading, setProjectLoading] = useState(Boolean(projectId));
  const [resolutionVersion, setResolutionVersion] = useState(0);
  const [assigneeFilter, setAssigneeFilter] = useState<TaskAssigneeFilter>("all");
  const [dueFilter, setDueFilter] = useState<TaskDueFilter>("all");
  const [tagIds, setTagIds] = useState<string[]>([]);
  const { me } = useMe(getToken);
  const preferences = useUserPreferences(getToken);
  const timezone = preferences.data?.preferences.timezone;
  const kanbanTasks = useKanbanTasks(getToken, project?.id ?? null, assigneeFilter, undefined, dueFilter, timezone, tagIds);
  const { create: createTask, update: updateTask } = useTasks(getToken);
  const {
    tags,
    create: createTag,
    archive: archiveTag,
    restore: restoreTag,
  } = useTags(getToken, project?.id ?? null);
  const { tenantUsers, refresh: refreshTenantUsers } = useTenantUsers(getToken);

  useEffect(() => {
    if (kanbanTasks.dueDateSupported === false) setDueFilter("all");
  }, [kanbanTasks.dueDateSupported]);
  useEffect(() => {
    if (kanbanTasks.tagFilterSupported === false) setTagIds([]);
  }, [kanbanTasks.tagFilterSupported]);
  useEffect(() => setTagIds([]), [project?.id]);

  useEffect(() => {
    let cancelled = false;

    if (!projectId) {
      setProject(null);
      setProjectLoading(false);
      return () => { cancelled = true; };
    }

    setProject(null);
    setProjectLoading(true);
    void getProject(projectId)
      .then((nextProject) => {
        if (!cancelled) setProject(nextProject);
      })
      .finally(() => {
        if (!cancelled) setProjectLoading(false);
      });

    return () => { cancelled = true; };
  }, [getProject, projectId, resolutionVersion]);

  const handleTaskStatusChange = useCallback(async (
    taskId: string,
    status: TaskStatus,
  ): Promise<boolean> => (
    kanbanTasks.moveTask(taskId, status, async () => {
      const updated = await updateTask(taskId, { status });
      if (updated) notifyTaskChanged(updated);
      return updated;
    })
  ), [kanbanTasks, updateTask]);

  const handleUpdateTask = useCallback(async (
    taskId: string,
    updates: TaskUpdateRequest,
  ): Promise<boolean> => {
    const updated = await updateTask(taskId, updates);
    if (!updated) return false;
    kanbanTasks.upsert(updated);
    notifyTaskChanged(updated);
    return true;
  }, [kanbanTasks, updateTask]);

  const handleCreateTask = useCallback(async (
    title: string,
    status: TaskStatus,
    assignedTo?: string | null,
    tagIds?: string[],
    dueDate?: string | null,
    titleSource?: TaskTitleSource,
  ): Promise<Task | null> => {
    if (!project) return null;

    let task = await createTask(
      project.id,
      title,
      assignedTo,
      tagIds,
      undefined,
      titleSource,
      status,
      dueDate,
    );
    if (!task) return null;
    kanbanTasks.upsert(task);
    notifyTaskChanged(task);

    // Older servers may ignore the optional status field, so retain the
    // ordinary PATCH fallback for a card created in another fixed-status lane.
    if (task.status !== status) {
      let movedTask: Task | null = null;
      const moved = await kanbanTasks.moveTask(task.id, status, async () => {
        movedTask = await updateTask(task!.id, { status });
        if (movedTask) notifyTaskChanged(movedTask);
        return movedTask;
      });
      if (!moved || !movedTask) return null;
      task = movedTask;
    }

    return task;
  }, [createTask, kanbanTasks, project, updateTask]);

  const handleDeleteTask = useCallback((taskId: string) => {
    void handleTaskStatusChange(taskId, "cancelled");
  }, [handleTaskStatusChange]);

  const handleCardClick = useCallback((task: Task) => {
    const sessionId = task.session_id?.trim();
    if (!sessionId) return;
    window.open(`/session/${sessionId}`, "_blank", "noopener");
  }, []);

  if (!projectId) {
    return (
      <ProjectKanbanUnavailable
        message={t("kanbanView.missingProject")}
        className={className}
      />
    );
  }

  if (projectLoading) {
    return (
      <section
        className={cn("flex h-full min-h-0 items-center justify-center gap-2 p-6 text-sm text-muted-foreground", className)}
        data-testid="project-kanban-loading"
      >
        <Loader2 className="size-4 animate-spin" aria-hidden="true" />
        {t("loadingProject")}
      </section>
    );
  }

  if (!project) {
    return (
      <ProjectKanbanUnavailable
        message={projectError ?? t("kanbanView.projectUnavailable")}
        onRetry={() => setResolutionVersion((version) => version + 1)}
        className={className}
      />
    );
  }

  const isEmpty = kanbanTasks.initialized && kanbanTasks.tasks.length === 0;

  return (
    <section
      className={cn("flex h-full min-h-0 min-w-0 flex-1 flex-col", className)}
      data-testid="project-kanban-view"
    >
      <header className="flex shrink-0 items-center gap-2 border-b px-4 py-3">
        <FolderKanban className="size-4 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold" title={project.name}>{project.name}</h2>
          {project.directive && (
            <p className="truncate text-xs text-muted-foreground" title={project.directive}>
              {project.directive}
            </p>
          )}
        </div>
      </header>
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b px-4 py-2">
        <TaskFilterMenu assignee={assigneeFilter} onAssigneeChange={setAssigneeFilter} due={dueFilter} onDueChange={setDueFilter}
          dueSupported={kanbanTasks.dueDateSupported} tagIds={tagIds} onTagIdsChange={setTagIds}
          tagSupported={kanbanTasks.tagFilterSupported} tags={tags} hasCurrentUser={Boolean(me?.user_id)}
          onAssigneePickerOpen={() => { void refreshTenantUsers().catch(() => {}); }} />
      </div>
      {isEmpty && (
        <p
          className="shrink-0 border-b px-4 py-2 text-sm text-muted-foreground"
          data-testid="project-kanban-empty"
        >
          {t("kanbanView.noTasks")}
        </p>
      )}
      <KanbanBoard
        key={project.id}
        columns={kanbanTasks.columns}
        onStatusChange={handleTaskStatusChange}
        onBulkAction={(request) => kanbanTasks.bulkAction(request, tags)}
        onCardClick={handleCardClick}
        onDeleteTask={handleDeleteTask}
        onCreateTask={handleCreateTask}
        onUpdateTask={handleUpdateTask}
        allTags={tags}
        onUpdateTags={(taskId, tagIds) => handleUpdateTask(taskId, { tags: tagIds })}
        onCreateTag={createTag}
        onArchiveTag={archiveTag}
        onRestoreTag={restoreTag}
        onLoadMore={kanbanTasks.loadMore}
        onRetryColumn={kanbanTasks.retry}
        onRevealPending={kanbanTasks.revealPending}
        onColumnAtTopChange={kanbanTasks.setColumnAtTop}
        tenantUsers={tenantUsers}
        defaultAssigneeId={me?.user_id}
        dueDateSupported={kanbanTasks.dueDateSupported === true}
        timezone={timezone}
        defaultAgentId={project.default_agent_id}
      />
    </section>
  );
}

function ProjectKanbanUnavailable({
  message,
  onRetry,
  className,
}: {
  message: string;
  onRetry?: () => void;
  className?: string;
}) {
  const { t } = useTranslation("projects");
  return (
    <section
      className={cn("flex h-full min-h-0 flex-col items-center justify-center gap-3 p-6 text-center", className)}
      data-testid="project-kanban-unavailable"
    >
      <AlertCircle className="size-5 text-destructive" aria-hidden="true" />
      <div>
        <p className="text-sm font-medium">{t("kanbanView.unavailable")}</p>
        <p className="mt-1 text-sm text-muted-foreground">{message}</p>
      </div>
      {onRetry && (
        <Button type="button" variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="size-3.5" aria-hidden="true" />
          {t("common:actions.retry")}
        </Button>
      )}
    </section>
  );
}
